'use client';

import { useQuery } from '@tanstack/react-query';
import { getInteractionAggregates } from '@/api/generated/interactions/interactions';
import type { TimeRangeConfig } from '@/api/generated/model';
import type { GenericFilter } from '@/comps/FilterPicker/FilterPicker';
import { QUERY_KEY_INTERACTION_AGGREGATES } from '@/constants/query-keys';
import { buildAiSpendOrgAggregatesPayload, buildAiSpendSegmentTrendPayload, buildAiSpendTeamAggregatesPayload, buildAiSpendTrendAggregatesPayload } from '@/domains/pages/ai-spend/utils/ai-spend-api-payloads';
import { filterTeamsWithTasks, mapOrgSpendSnapshot, mapTeamSpendSnapshots, type AiSpendOrgSnapshot, type TeamSpendSnapshot } from '@/domains/pages/ai-spend/utils/map-ai-spend-aggregates';
import { buildSpendPeerBand, mapSegmentSpendTrend, type SpendPeerBandPoint } from '@/domains/pages/ai-spend/utils/map-ai-spend-segment-trend';
import { mapSpendTrendSeries, type SpendTrendSeries } from '@/domains/pages/ai-spend/utils/map-ai-spend-trend';
import { NEBULY_PAGE_QUERY_OPTIONS, getPageTimeRangeQueryKey } from '@/domains/pages/shared/page-query-options';
import { resolveNebulyPageTimeRange } from '@/domains/pages/shared/page-time-range';
import { getTrendQuery } from '@/domains/pages/shared/trend-query';
import { useNebulyPageEffectiveNow } from '@/domains/pages/hooks/use-nebuly-page-effective-now';
import { useNebulyPageStableNow } from '@/domains/pages/hooks/use-nebuly-page-stable-now';
import { getTimeRangeMemoKey } from '@/domains/pages/utils/nebuly-page-time-range';
import { useCallback, useMemo } from 'react';
const EMPTY_SPEND_TREND: SpendTrendSeries = {
  labels: [],
  totalSpend: [],
  spendPerTask: []
};
export type AiSpendPageData = {
  org: AiSpendOrgSnapshot;
  teamsForCostPerTask: TeamSpendSnapshot[];
  spendTrend: SpendTrendSeries;
  peerBand: SpendPeerBandPoint[];
  isLoading: boolean;
  isError: boolean;
  error: unknown;
  refetch: () => void;
};
type UseAiSpendPageDataParams = {
  projectId: string | undefined;
  timeRange: TimeRangeConfig | undefined;
  filters?: GenericFilter[];
  specialFilters?: GenericFilter[];
  enabled?: boolean;
};
export function useAiSpendPageData({
  projectId,
  timeRange,
  filters = [],
  specialFilters = [],
  enabled: enabledParam = true
}: UseAiSpendPageDataParams): AiSpendPageData {
  const stableNow = useNebulyPageStableNow();
  const {
    effectiveNow,
    isReady: isEffectiveNowReady
  } = useNebulyPageEffectiveNow();
  const stableNowKey = stableNow.getTime();
  const effectiveNowKey = effectiveNow.getTime();
  const timeRangeMemoKey = getTimeRangeMemoKey(timeRange);
  const resolvedTimeRange = useMemo(() => resolveNebulyPageTimeRange(timeRange, stableNow, effectiveNow), [timeRangeMemoKey, stableNowKey, effectiveNowKey]);
  const trendQuery = useMemo(() => getTrendQuery(resolvedTimeRange, stableNow, effectiveNow), [timeRangeMemoKey, resolvedTimeRange, stableNowKey, effectiveNowKey]);
  const timeRangeQueryKey = useMemo(() => getPageTimeRangeQueryKey(resolvedTimeRange), [resolvedTimeRange]);
  const trendQueryKey = useMemo(() => ({
    granularity: trendQuery.granularity,
    bucketKind: trendQuery.bucketKind,
    timeRange: getPageTimeRangeQueryKey(trendQuery.time_range)
  }), [trendQuery]);
  const filtersQueryKey = useMemo(() => JSON.stringify(filters), [filters]);
  const specialFiltersQueryKey = useMemo(() => JSON.stringify(specialFilters), [specialFilters]);
  const enabled = enabledParam && Boolean(projectId && resolvedTimeRange && isEffectiveNowReady);
  const orgQuery = useQuery({
    queryKey: [QUERY_KEY_INTERACTION_AGGREGATES, 'ai-spend-org', projectId, timeRangeQueryKey, filtersQueryKey, specialFiltersQueryKey],
    queryFn: ({
      signal
    }) => getInteractionAggregates(buildAiSpendOrgAggregatesPayload({
      projectId: projectId!,
      timeRange: resolvedTimeRange!,
      filters,
      specialFilters
    }), {
      signal
    }),
    enabled,
    ...NEBULY_PAGE_QUERY_OPTIONS
  });
  const orgReady = enabled && orgQuery.isSuccess;
  const teamsQuery = useQuery({
    queryKey: [QUERY_KEY_INTERACTION_AGGREGATES, 'ai-spend-teams', projectId, timeRangeQueryKey, filtersQueryKey, specialFiltersQueryKey],
    queryFn: ({
      signal: signal_0
    }) => getInteractionAggregates(buildAiSpendTeamAggregatesPayload({
      projectId: projectId!,
      timeRange: resolvedTimeRange!,
      filters,
      specialFilters
    }), {
      signal: signal_0
    }),
    enabled: orgReady,
    ...NEBULY_PAGE_QUERY_OPTIONS
  });
  const trendQueryResult = useQuery({
    queryKey: [QUERY_KEY_INTERACTION_AGGREGATES, 'ai-spend-trend', projectId, trendQueryKey, filtersQueryKey, specialFiltersQueryKey],
    queryFn: ({
      signal: signal_1
    }) => getInteractionAggregates(buildAiSpendTrendAggregatesPayload({
      projectId: projectId!,
      trendQuery,
      filters,
      specialFilters
    }), {
      signal: signal_1
    }),
    enabled: orgReady,
    ...NEBULY_PAGE_QUERY_OPTIONS
  });
  const teamTrendQuery = useQuery({
    queryKey: [QUERY_KEY_INTERACTION_AGGREGATES, 'ai-spend-team-trend', projectId, trendQueryKey, filtersQueryKey, specialFiltersQueryKey],
    queryFn: ({
      signal: signal_2
    }) => getInteractionAggregates(buildAiSpendSegmentTrendPayload({
      projectId: projectId!,
      trendQuery,
      filters,
      specialFilters,
      kind: 'team'
    }), {
      signal: signal_2
    }),
    enabled: orgReady,
    ...NEBULY_PAGE_QUERY_OPTIONS
  });
  const org = useMemo(() => orgQuery.data ? mapOrgSpendSnapshot(orgQuery.data) : null, [orgQuery.data]);
  const teamsForCostPerTask = useMemo(() => {
    if (!teamsQuery.data || org?.totalCost == null) return [];
    return filterTeamsWithTasks(mapTeamSpendSnapshots(teamsQuery.data, org.totalCost));
  }, [org?.totalCost, teamsQuery.data]);
  const spendTrend = useMemo(() => trendQueryResult.data ? mapSpendTrendSeries(trendQueryResult.data, trendQuery) : EMPTY_SPEND_TREND, [trendQuery, trendQueryResult.data]);
  const teamTrend = useMemo(() => {
    if (!teamTrendQuery.data) {
      return {
        labels: [] as string[],
        totalSpendBySegment: {} as Record<string, Array<number | null>>,
        spendPerTaskBySegment: {} as Record<string, Array<number | null>>
      };
    }
    return mapSegmentSpendTrend(teamTrendQuery.data, trendQuery);
  }, [teamTrendQuery.data, trendQuery]);
  const peerBand = useMemo(() => buildSpendPeerBand(teamTrend.spendPerTaskBySegment, spendTrend.labels.length), [spendTrend.labels.length, teamTrend.spendPerTaskBySegment]);
  const isLoading = orgQuery.isLoading || orgReady && (teamsQuery.isLoading || trendQueryResult.isLoading || teamTrendQuery.isLoading);
  const isError = orgQuery.isError || teamsQuery.isError || trendQueryResult.isError || teamTrendQuery.isError;
  const error = orgQuery.error ?? teamsQuery.error ?? trendQueryResult.error ?? teamTrendQuery.error;
  const refetch = useCallback(() => {
    void orgQuery.refetch();
    void teamsQuery.refetch();
    void trendQueryResult.refetch();
    void teamTrendQuery.refetch();
  }, [orgQuery.refetch, teamsQuery.refetch, trendQueryResult.refetch, teamTrendQuery.refetch]);
  return {
    org: org ?? {
      totalCost: null,
      totalCostPrev: null,
      tasks: 0,
      spendPerTask: null
    },
    teamsForCostPerTask,
    spendTrend,
    peerBand,
    isLoading,
    isError,
    error,
    refetch
  };
}